import React from 'react';
import { formatDate } from '../../lib/dateUtils';

interface BalanceSheetPrintProps {
  data: {
    company: {
      name: string;
      address: string;
    };
    reportMeta: {
      toDate: string;
    };
    assets: { name: string, amount: number, type: string }[];
    liabilities: { name: string, amount: number, type: string }[];
    totalAssets: string;
    totalLiabilities: string;
  };
}

const BalanceSheetPrint: React.FC<BalanceSheetPrintProps> = ({ data }) => {
  const maxRows = Math.max(data.assets.length, data.liabilities.length);
  const rows = Array.from({ length: maxRows });

  return (
    <div className="print-report p-6 text-black font-sans text-[12px] bg-white ">
      {/* Header */}
      <div className="text-center border-b-2 border-black pb-4 mb-4">
        <h1 className="text-2xl font-bold uppercase">{data.company.name}</h1>
        <p className="text-[14px]">{data.company.address}</p>
        <h2 className="text-lg font-bold mt-2 underline">Balance Sheet</h2>
        <p className="font-semibold">as at {formatDate(data.reportMeta.toDate)}</p>
      </div>

      {/* Table */}
      <div className="flex w-full border border-black">
        {/* Liabilities Side */}
        <div className="w-1/2 border-r border-black flex flex-col">
          <div className="bg-gray-100 border-b border-black px-4 py-2 font-bold flex justify-between">
            <span>Liabilities</span>
            <span>Amount</span>
          </div>
          <div className="flex-1">
            {data.liabilities.map((item, idx) => (
              <div key={idx} className="flex justify-between px-4 py-1 border-b border-gray-100 last:border-0">
                <span className="font-bold uppercase tracking-tight">{item.name}</span>
                <span className="font-semibold">{item.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="bg-gray-50 border-t border-black px-4 py-2 font-bold flex justify-between mt-auto">
            <span>Total</span>
            <span>{data.totalLiabilities}</span>
          </div>
        </div>

        {/* Assets Side */}
        <div className="w-1/2 flex flex-col">
          <div className="bg-gray-100 border-b border-black px-4 py-2 font-bold flex justify-between">
            <span>Assets</span>
            <span>Amount</span>
          </div>
          <div className="flex-1">
            {data.assets.map((item, idx) => (
              <div key={idx} className="flex justify-between px-4 py-1 border-b border-gray-100 last:border-0">
                <span className="font-bold uppercase tracking-tight">{item.name}</span>
                <span className="font-semibold">{item.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="bg-gray-50 border-t border-black px-4 py-2 font-bold flex justify-between mt-auto">
            <span>Total</span>
            <span>{data.totalAssets}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 flex justify-between">
        <div className="text-center">
          <div className="border-t border-black w-32 mt-8 pt-1">Prepared By</div>
        </div>
        <div className="text-center">
          <div className="border-t border-black w-32 mt-8 pt-1">Authorised Signatory</div>
        </div>
      </div>
    </div>
  );
};

export default BalanceSheetPrint;
